import type { EtrnCargoItem } from "./etrn-generation.types";
import type { EzzCargoItem } from "./ezz-generation.types";

/**
 * Строка груза в настройках робота БП (JSON в cargo_items).
 * Поля соответствуют форме 1С Доки / Астрал ЭПД.
 */
export interface BpCargoItemRow {
  /** Отгрузочное наименование груза * */
  name: string;
  /** Состояние груза * */
  condition: string;
  /** Способ упаковки (СпУпак) — для ЭТрН * */
  packaging: string;
  /** Вид тары / ОКВГУМ (ВидТар) * */
  packageKind: string;
  /** Количество грузовых мест * */
  placesCount: string;
  /** Объем грузовых мест * */
  volume: string;
  /** Метод определения массы (МетОпрМасс) * */
  massMethod: string;
  /** Распределение груза: 0/1 * */
  canDistribute: string;
  /** Делимость груза: 0/1 * */
  isDivisible: string;
  /** Масса брутто, кг * */
  grossMass: string;
  /** Высота, м * */
  height: string;
  /** Длина, м * */
  length: string;
  /** Ширина, м * */
  width: string;
  /** Масса нетто (необяз.) */
  netMass: string;
  /** Количество паллет (необяз.) */
  palletsCount: string;
  /** Масса тары (необяз.) */
  tareMass: string;
  /** Масса грузовых мест, кг (необяз.) */
  placesMass: string;
  /** Информация о приеме гружёного контейнера (необяз.) */
  containerInfo: string;
  /** Участие водителя (необяз.) */
  driverParticipation: string;
  /** Другая необходимая информация о грузе (необяз.) */
  otherInfo: string;
}

export function emptyBpCargoItemRow(): BpCargoItemRow {
  return {
    name: "",
    condition: "Нормальное",
    packaging: "Коробки",
    packageKind: "00",
    placesCount: "",
    volume: "",
    massMethod: "01",
    canDistribute: "0",
    isDivisible: "1",
    grossMass: "",
    height: "",
    length: "",
    width: "",
    netMass: "",
    palletsCount: "",
    tareMass: "",
    placesMass: "",
    containerInfo: "",
    driverParticipation: "",
    otherInfo: "",
  };
}

function optionalCargoString(value: string): string | undefined {
  const trimmed = String(value || "").trim();
  return trimmed || undefined;
}

function rowField(row: Record<string, unknown>, key: string, fallback = ""): string {
  if (row[key] !== undefined && row[key] !== null) {
    return String(row[key]);
  }
  return fallback;
}

export function parseBpCargoItemsJson(raw: unknown): BpCargoItemRow[] {
  const text = Array.isArray(raw) ? String(raw[0] ?? "") : String(raw ?? "");
  if (!text.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(text) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map((item) => {
        if (!item || typeof item !== "object") {
          return null;
        }
        const row = item as Record<string, unknown>;
        const defaults = emptyBpCargoItemRow();
        const packaging = rowField(row, "packaging", defaults.packaging);
        const packageKindRaw = rowField(row, "packageKind");
        const packageKind =
          packageKindRaw ||
          (/^[0-9A-Za-z]{2}$/.test(String(row.packaging || "").trim())
            ? String(row.packaging).trim()
            : defaults.packageKind);
        return {
          name: rowField(row, "name"),
          condition: rowField(row, "condition", defaults.condition),
          packaging,
          packageKind,
          placesCount: rowField(row, "placesCount"),
          volume: rowField(row, "volume"),
          massMethod: rowField(row, "massMethod", defaults.massMethod),
          canDistribute: rowField(row, "canDistribute", defaults.canDistribute),
          isDivisible: rowField(row, "isDivisible", defaults.isDivisible),
          grossMass: rowField(row, "grossMass"),
          height: rowField(row, "height"),
          length: rowField(row, "length"),
          width: rowField(row, "width"),
          netMass: rowField(row, "netMass"),
          palletsCount: rowField(row, "palletsCount"),
          tareMass: rowField(row, "tareMass"),
          placesMass: rowField(row, "placesMass"),
          containerInfo: rowField(row, "containerInfo"),
          driverParticipation: rowField(row, "driverParticipation"),
          otherInfo: rowField(row, "otherInfo"),
        } satisfies BpCargoItemRow;
      })
      .filter((item): item is BpCargoItemRow => item !== null);
  } catch {
    return [];
  }
}

function propertyAsString(
  properties: Record<string, string | string[]>,
  key: string
): string {
  const raw = properties[key];
  if (Array.isArray(raw)) {
    return String(raw[0] ?? "").trim();
  }
  return String(raw ?? "").trim();
}

function parseDimensions(raw: string): {
  length?: string;
  width?: string;
  height?: string;
} {
  const parts = String(raw || "")
    .split(/[*xх×]/i)
    .map((part) => part.trim().replace(",", "."))
    .filter(Boolean);
  if (parts.length < 3) {
    return {};
  }
  return {
    length: parts[0],
    width: parts[1],
    height: parts[2],
  };
}

/**
 * Груз только из свойств робота: JSON cargo_items или плоские cargo_*.
 * Без чтения CRM / смарт-процессов портала.
 */
export function parseBpCargoItemsFromRobotProperties(
  properties: Record<string, string | string[]>
): BpCargoItemRow[] {
  const fromJson = parseBpCargoItemsJson(properties.cargo_items);
  if (fromJson.length) {
    return fromJson;
  }

  const name = propertyAsString(properties, "cargo_name");
  if (!name) {
    return [];
  }
  const defaults = emptyBpCargoItemRow();
  const dims = parseDimensions(propertyAsString(properties, "cargo_dimensions"));
  return [
    {
      ...defaults,
      name,
      placesCount: propertyAsString(properties, "cargo_places_count"),
      grossMass: propertyAsString(properties, "cargo_gross_mass"),
      volume: propertyAsString(properties, "cargo_volume"),
      packaging:
        propertyAsString(properties, "cargo_packaging") || defaults.packaging,
      packageKind:
        propertyAsString(properties, "cargo_packaging_kind") ||
        defaults.packageKind,
      condition:
        propertyAsString(properties, "cargo_condition") || defaults.condition,
      massMethod:
        propertyAsString(properties, "cargo_mass_method") || defaults.massMethod,
      canDistribute:
        propertyAsString(properties, "cargo_can_distribute") ||
        defaults.canDistribute,
      length: dims.length || "",
      width: dims.width || "",
      height: dims.height || "",
    },
  ];
}

export function bpCargoRowsToEtrnCargo(rows: BpCargoItemRow[]): EtrnCargoItem[] {
  return rows
    .map((row) => {
      const name = optionalCargoString(row.name);
      if (!name) {
        return null;
      }
      return {
        name,
        condition: optionalCargoString(row.condition) || "Нормальное",
        placesCount: optionalCargoString(row.placesCount),
        grossMass: optionalCargoString(row.grossMass),
        netMass: optionalCargoString(row.netMass),
        packaging: optionalCargoString(row.packaging),
        packageKind: optionalCargoString(row.packageKind) || "00",
      } satisfies EtrnCargoItem;
    })
    .filter((item): item is EtrnCargoItem => item !== null);
}

export function bpCargoRowsToEzzCargo(rows: BpCargoItemRow[]): EzzCargoItem[] {
  return rows
    .map((row) => {
      const name = optionalCargoString(row.name);
      if (!name) {
        return null;
      }
      const canDistribute = optionalCargoString(row.canDistribute) === "1" ? "1" : "0";
      const isDivisible = optionalCargoString(row.isDivisible) === "0" ? "0" : "1";
      return {
        name,
        condition: optionalCargoString(row.condition) || "Нормальное",
        packageKind: optionalCargoString(row.packageKind) || "00",
        placesCount: optionalCargoString(row.placesCount),
        grossMass: optionalCargoString(row.grossMass),
        volume: optionalCargoString(row.volume),
        height: optionalCargoString(row.height),
        length: optionalCargoString(row.length),
        width: optionalCargoString(row.width),
        massMethod: optionalCargoString(row.massMethod) || "01",
        canDistribute,
        isDivisible,
      } satisfies EzzCargoItem;
    })
    .filter((item): item is EzzCargoItem => item !== null);
}

export function serializeBpCargoItems(rows: BpCargoItemRow[]): string {
  return JSON.stringify(rows);
}

/** Обязательные поля позиции груза для робота ЭЗЗ. */
export const EZZ_CARGO_REQUIRED_KEYS = [
  "name",
  "condition",
  "packageKind",
  "placesCount",
  "volume",
  "massMethod",
  "canDistribute",
  "isDivisible",
  "grossMass",
  "height",
  "length",
  "width",
] as const;

/** Обязательные поля позиции груза для робота ЭТрН. */
export const ETRN_CARGO_REQUIRED_KEYS = [
  "name",
  "condition",
  "packaging",
  "packageKind",
  "placesCount",
  "grossMass",
] as const;
